import { grownFormLabel, toCanonical, UNITS, type UnitId, withGrownForm } from "@bl/domain";
import { Check, ClipboardCheck, Plus, RefreshCw, TriangleAlert, Truck, X } from "lucide-react";
import { useId, useState } from "react";
import { type BrewDetail, send, useChange } from "../api";
import {
  amountOf,
  assignedQuantity,
  type BrewLine,
  type DraftPull,
  decimalOnly,
  holdsIn,
  inUnit,
  type Lot,
  type LotStanding,
  lotKey,
  lotsOf,
  NONE,
  newPullKey,
  overBy,
  type Pull,
  repitches,
  shortOf,
  shown,
  unitOf,
  unitOfPull,
} from "../brewPulls";
import { ErrorNote } from "../components/ui";
import { useNotify } from "../notify";
import { HOP_USE_LABELS, showFahrenheit } from "../recipeFormat";
import { HarvestedFacts, RepitchFrom } from "./Repitch";

/**
 * A lot as a picker lists it, in its own unit, most telling first, since a phone shows only the start
 * of it until the picker opens: a yeast's harvest by its generation, then its code, its alpha, how much
 * is there, its brand, and where.
 */
function lotLabel(lot: Lot) {
  return [
    lot.generation === null ? null : `G${lot.generation} harvest`,
    lot.lotCode,
    lot.alphaPct === null ? null : `${lot.alphaPct}% alpha`,
    `${inUnit(lot.onHand, lot.unit)} ${UNITS[lot.unit].label}`,
    lot.brand,
    lot.locationName,
  ]
    .filter(Boolean)
    .join(" · ");
}
/** A line as a list names it: its item and a hop's form, and when it goes in where that tells it apart. */
export const lineName = (l: BrewLine) =>
  `${withGrownForm(l.item?.name ?? l.name, l.item?.hopForm)}${whenOf(l) ? ` (${whenOf(l)})` : ""}`;
/** When a hop goes in, which is what tells one Citra line from the next. */
export function whenOf(line: BrewLine): string | null {
  if (line.kind !== "hop" || !line.hopUse) return null;
  const use = HOP_USE_LABELS[line.hopUse] ?? line.hopUse;
  const n = Number(line.hopUse === "dry_hop" ? line.days : line.minutes);
  // A whirlpool hop at the recipe's temperature, which brew day's own sits beside.
  const at = line.tempC === null ? "" : ` at ${showFahrenheit(line.tempC)}`;
  if (!n) return `${use}${at}`;
  return line.hopUse === "dry_hop"
    ? `${use}, ${n} ${n === 1 ? "day" : "days"}`
    : `${use}, ${n} min${at}`;
}
/** One linked line: what it needs, and which lots it comes from. */
export function PullCard({
  line,
  brew,
  pulls,
  readOnly,
  standing,
  onChange,
  onReplan,
  onStock,
  onAssign,
}: {
  line: BrewLine;
  /** For a yeast, the brew it is pitched into: the beers it can be repitched from are listed. */
  brew?: BrewDetail;
  pulls: DraftPull[];
  readOnly: boolean;
  standing: Map<string, LotStanding>;
  onChange: (pulls: DraftPull[]) => void;
  onReplan: () => void;
  onStock: (mode: "receive" | "adjust") => void;
  onAssign: Assign;
}) {
  const [counting, setCounting] = useState<string | null>(null);
  const unit = unitOf(line);
  const label = UNITS[unit].label;
  const lots = lotsOf(line);
  // A yeast's harvest is weighed where its pitch is not: a pull reads in its own lot's unit, and
  // moving it to a lot measured another way starts its amount again.
  const startOn = (lot: Lot | undefined, short: number) =>
    lot && repitches(line, lot)
      ? inUnit(lot.onHand, lot.unit)
      : short > 0
        ? shown(short)
        : line.needed !== null
          ? inUnit(line.needed, unit)
          : "";
  const short = shortOf(line, pulls);
  const anyOver = pulls.some((p) => overBy(standing.get(p.lotKey)) > 0);
  const anyNone = pulls.some((p) => p.lotKey === NONE);
  const edit = (i: number, patch: Partial<DraftPull>) =>
    onChange(pulls.map((p, j) => (j === i ? { ...p, ...patch } : p)));
  // Another lot starts on the first one not already in use, with what is still short.
  const next = lots.find((lot) => !pulls.some((p) => p.lotKey === lotKey(lot))) ?? lots[0];

  const head = (
    <>
      <div className="pull-head">
        <span>
          <strong>{line.item?.name ?? line.name}</strong>
          {/* Cryo goes in at a different amount than T-90, and both read "Citra". */}
          {grownFormLabel(line.item?.hopForm) ? (
            <span className="faint"> · {grownFormLabel(line.item?.hopForm)}</span>
          ) : null}
        </span>
        <span className="num">
          {line.needed === null ? "" : `${inUnit(line.needed, unit)} ${label}`}
        </span>
      </div>
      {/* Not what the recipe's file called it: a linked line goes by its item (Scott's Jam, 2026-10-08). */}
      {whenOf(line) ? (
        <div className="pull-sub">
          <span className="pull-when">{whenOf(line)}</span>
        </div>
      ) : null}
    </>
  );

  // Once brewed, what was taken, as it was taken: no pickers, and no lot balances from after it. What
  // was not in inventory waits to be assigned to its lot (Scott, 2026-10-02).
  if (readOnly) {
    return (
      <section className="brew-card pull-card">
        {head}
        {line.pulls.length === 0 ? <p className="muted">None taken.</p> : null}
        {line.pulls
          .filter((p) => p.lotId !== null)
          .map((p) => (
            <div key={p.id} className="pull-row pull-taken">
              <span>
                {[
                  p.generation === null ? null : `G${p.generation} harvest`,
                  p.lotCode,
                  p.alphaPct === null ? null : `${p.alphaPct}% alpha`,
                  p.brand,
                  p.locationName,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
              <span className="num">
                {inUnit(p.quantity, p.unit)} {UNITS[p.unit].label}
              </span>
            </div>
          ))}
        {line.pulls
          .filter((p) => p.lotId === null)
          .map((p) => (
            <ToAssign key={p.id} line={line} pull={p} onStock={onStock} onAssign={onAssign} />
          ))}
      </section>
    );
  }

  // A yeast that could come from a beer in a fermenter is weighed from it on the day: until it is,
  // the line is waiting on that, not short of a pitch.
  const fromTank = brew !== undefined && line.repitchFrom.length > 0;
  return (
    <section className="brew-card pull-card" id={`pull-${line.recipeLineId}`}>
      {head}
      {fromTank ? (
        <RepitchFrom
          brew={brew}
          line={line}
          // The first beer past no limit is ready to weigh when nothing is pitched yet.
          ready={pulls.length === 0}
          onPitched={(lotKey, amount) => onChange([{ key: newPullKey(), lotKey, amount }])}
        />
      ) : null}
      {fromTank && (lots.length > 0 || pulls.length > 0) ? (
        <div className="repitch-group">From inventory</div>
      ) : null}
      {lots.length === 0 && !fromTank ? (
        <p className="pull-warning">None in inventory to take it from.</p>
      ) : null}
      {pulls.map((p, i) => {
        const lot = standing.get(p.lotKey);
        const over = overBy(lot);
        const chosen = lots.find((l) => lotKey(l) === p.lotKey);
        const pullUnit = unitOfPull(line, p.lotKey);
        const pullLabel = UNITS[pullUnit].label;
        return (
          <div key={p.key} className="pull-row">
            <select
              className="select"
              aria-label={`Lot of ${line.name}`}
              value={p.lotKey}
              onChange={(ev) => {
                const key = ev.target.value;
                const to = lots.find((l) => lotKey(l) === key);
                edit(
                  i,
                  unitOfPull(line, key) === pullUnit
                    ? { lotKey: key }
                    : { lotKey: key, amount: startOn(to, shortOf(line, [])) },
                );
              }}
            >
              {lots.map((lot) => (
                <option key={lotKey(lot)} value={lotKey(lot)}>
                  {lotLabel(lot)}
                </option>
              ))}
              <option value={NONE}>Not in inventory</option>
            </select>
            <div className="pull-amount">
              <span className="input-unit">
                <input
                  className="input num"
                  inputMode="decimal"
                  enterKeyHint="done"
                  autoComplete="off"
                  aria-label={`How much ${line.name}, in ${pullLabel}`}
                  value={p.amount}
                  onChange={(ev) => edit(i, { amount: decimalOnly(ev.target.value) })}
                />
                <span className="unit">{pullLabel}</span>
              </span>
              <button
                type="button"
                className="icon-button"
                aria-label="Take none from this lot"
                title="Take none from this lot"
                onClick={() => onChange(pulls.filter((_, j) => j !== i))}
              >
                <X size={20} />
              </button>
            </div>
            {p.lotKey === NONE ? (
              <div className="pull-note">
                Brewed records it on the brew, to assign to its lot once it is received or counted.
              </div>
            ) : null}
            {chosen?.repitch ? <HarvestedFacts lot={chosen} facts={chosen.repitch} /> : null}
            {over > 0 ? (
              <>
                <div className="pull-warning">
                  <TriangleAlert size={15} /> {shown(over / Number(UNITS[pullUnit].factor))}{" "}
                  {pullLabel} more than this lot has here
                  {(lot?.lineIds.size ?? 0) > 1 ? ", with the other lines taking from it" : ""}.
                  Brewed takes what it has and records the rest as not in inventory.
                </div>
                {counting === p.key && chosen && line.item ? (
                  <CountLot lot={chosen} unit={pullUnit} onDone={() => setCounting(null)} />
                ) : (
                  <div className="pull-fixes">
                    <button
                      type="button"
                      className="button"
                      title="There is more on the shelf than inventory says"
                      onClick={() => setCounting(p.key)}
                    >
                      <ClipboardCheck size={16} /> Count it
                    </button>
                  </div>
                )}
              </>
            ) : null}
          </div>
        );
      })}
      {short > 0 && fromTank && pulls.length === 0 ? (
        <div className="pull-note">
          Nothing pitched yet: weigh it from a fermenter above, or take it from a lot.
        </div>
      ) : short > 0 ? (
        <div className="pull-warning">
          {shown(short, 2)} {label} short of the recipe
        </div>
      ) : null}
      <div className="pull-fixes">
        <button
          type="button"
          className="button"
          onClick={() =>
            onChange([
              ...pulls,
              {
                key: newPullKey(),
                lotKey: next ? lotKey(next) : NONE,
                amount:
                  next && repitches(line, next)
                    ? startOn(next, short)
                    : short > 0
                      ? shown(short)
                      : "",
              },
            ])
          }
        >
          <Plus size={16} /> {pulls.length ? "Another lot" : "Take from a lot"}
        </button>
        {anyOver ? (
          <button
            type="button"
            className="button"
            title="Plan this ingredient again, oldest lot first, from what is on hand now"
            onClick={onReplan}
          >
            <RefreshCw size={16} /> Replan
          </button>
        ) : null}
        {anyOver || anyNone ? (
          <button
            type="button"
            className="button"
            title="A delivery that has not been entered yet"
            onClick={() => onStock("receive")}
          >
            <Truck size={16} /> Receive a delivery
          </button>
        ) : null}
      </div>
    </section>
  );
}
type Assign = (
  input: { pullId: string; lotId: string; locationId: string; quantity: string },
  key: string,
) => Promise<unknown>;
/**
 * Stock a brewed brew used that inventory did not have, waiting for the lot it came from (Scott,
 * 2026-10-02). Once the delivery is received or the shelf counted, the lot is in inventory, and
 * assigning takes it from there as the brew's. A lot gives no more than it holds; the rest waits on.
 */
function ToAssign({
  line,
  pull,
  onStock,
  onAssign,
}: {
  line: BrewLine;
  pull: Pull;
  onStock: (mode: "receive" | "adjust") => void;
  onAssign: Assign;
}) {
  const id = useId();
  const notify = useNotify();
  const unit = pull.unit;
  const label = UNITS[unit].label;
  const lots = line.options;
  const [assigning, setAssigning] = useState(false);
  const [choice, setChoice] = useState("");
  const [amount, setAmount] = useState("");
  const chosen = lots.find((l) => lotKey(l) === choice);
  // A yeast's harvest is weighed where the pitch it stands in for is not: what was pitched of it is
  // typed in pounds, and settles the pull whatever the pitch was (Tyler, 2026-10-07).
  const lotUnit = chosen?.unit ?? unit;
  const lotLabelUnit = UNITS[lotUnit].label;
  const start = (lot: Lot | undefined) => {
    setChoice(lot ? lotKey(lot) : "");
    setAmount(
      !lot
        ? ""
        : repitches(line, lot)
          ? ""
          : shown(Math.min(Number(inUnit(pull.quantity, unit)), holdsIn(lot, unit))),
    );
  };
  const assign = useChange(
    (_: undefined, key) => {
      if (!chosen || !line.item) throw new Error("Choose the lot it came from");
      return onAssign(
        {
          pullId: pull.id,
          lotId: chosen.lotId,
          locationId: chosen.locationId,
          quantity: repitches(line, chosen)
            ? toCanonical({ value: String(amountOf(amount)), unit: chosen.unit }, chosen.dimension)
            : assignedQuantity(pull, chosen, amountOf(amount), unit, line.item.dimension),
        },
        key,
      );
    },
    {
      onSuccess: () => {
        notify(
          `Assigned ${shown(amountOf(amount))} ${lotLabelUnit} to ${chosen?.lotCode ?? "its lot"}`,
        );
        setAssigning(false);
      },
    },
  );
  const beyond = chosen ? amountOf(amount) - holdsIn(chosen, lotUnit) > 0.0005 : false;
  return (
    <div className="pull-row pull-open">
      <div className="pull-open-head">
        <span className="pull-flag">
          <TriangleAlert size={15} /> Not in inventory
        </span>
        <span className="num">
          {inUnit(pull.quantity, unit)} {label}
        </span>
      </div>
      {assigning ? (
        <form
          className="pull-count"
          onSubmit={(ev) => {
            ev.preventDefault();
            if (chosen && amountOf(amount) > 0) assign.mutate(undefined);
          }}
        >
          <label htmlFor={id}>Which lot did it come from?</label>
          <select
            id={id}
            className="select"
            value={choice}
            onChange={(ev) => start(lots.find((l) => lotKey(l) === ev.target.value))}
          >
            {lots.map((lot) => (
              <option key={lotKey(lot)} value={lotKey(lot)}>
                {lotLabel(lot)}
              </option>
            ))}
          </select>
          <div className="pull-amount">
            <span className="input-unit">
              <input
                className="input num"
                inputMode="decimal"
                enterKeyHint="done"
                autoComplete="off"
                aria-label={`How much of it, in ${lotLabelUnit}`}
                value={amount}
                onChange={(ev) => setAmount(decimalOnly(ev.target.value))}
              />
              <span className="unit">{lotLabelUnit}</span>
            </span>
            <button
              type="button"
              className="icon-button"
              aria-label="Don't assign it"
              title="Don't assign it"
              onClick={() => setAssigning(false)}
            >
              <X size={20} />
            </button>
          </div>
          {beyond && chosen ? (
            <div className="pull-warning">
              {chosen.lotCode} has {inUnit(chosen.onHand, lotUnit)} {lotLabelUnit} here, so that is
              all it gives
              {repitches(line, chosen) ? "." : "; the rest stays to assign."}
            </div>
          ) : null}
          <button
            type="submit"
            className="button primary"
            disabled={!chosen || amountOf(amount) <= 0 || assign.isPending}
          >
            Assign
          </button>
          <ErrorNote error={assign.error} />
        </form>
      ) : (
        <>
          {lots.length === 0 ? (
            <p className="muted">
              None in inventory to assign it to yet. Receive the delivery it came in, or count it.
            </p>
          ) : null}
          <div className="pull-fixes">
            {lots.length ? (
              <button
                type="button"
                className="button primary"
                onClick={() => {
                  start(lots[0]);
                  setAssigning(true);
                }}
              >
                <Check size={16} /> Assign to a lot
              </button>
            ) : null}
            <button
              type="button"
              className="button"
              title="A delivery that has not been entered yet"
              onClick={() => onStock("receive")}
            >
              <Truck size={16} /> Receive a delivery
            </button>
            <button
              type="button"
              className="button"
              title="There is more on the shelf than inventory says"
              onClick={() => onStock("adjust")}
            >
              <ClipboardCheck size={16} /> Count it
            </button>
          </div>
        </>
      )}
    </div>
  );
}
/**
 * A count of one lot where it sits, for when the shelf holds more than inventory says. It is recorded as
 * any count is: the difference from what the counter was shown, so it commutes with anything recorded
 * elsewhere meanwhile (docs/architecture.md). Not for a delivery that was never entered: receiving that
 * later would count it twice.
 */
function CountLot({ lot, unit, onDone }: { lot: Lot; unit: UnitId; onDone: () => void }) {
  const id = useId();
  const [counted, setCounted] = useState("");
  const notify = useNotify();
  const label = UNITS[unit].label;
  const count = useChange(
    (_: undefined, key) =>
      send.movement(
        {
          kind: "adjust",
          itemId: lot.itemId,
          locationId: lot.locationId,
          lotId: lot.lotId,
          counted: { value: String(amountOf(counted)), unit },
          expected: lot.onHand,
          note: "Counted on brew day",
        },
        key,
      ),
    {
      onSuccess: () => {
        notify(`Counted ${shown(amountOf(counted))} ${label} of ${lot.lotCode}`);
        onDone();
      },
    },
  );
  return (
    <form
      className="pull-count"
      onSubmit={(ev) => {
        ev.preventDefault();
        if (counted.trim() !== "") count.mutate(undefined);
      }}
    >
      <label htmlFor={id}>
        How much of {lot.lotCode} is in the {lot.locationName}? Inventory says{" "}
        {inUnit(lot.onHand, unit)} {label}.
      </label>
      <div className="pull-amount">
        <span className="input-unit">
          <input
            id={id}
            className="input num"
            inputMode="decimal"
            enterKeyHint="done"
            autoComplete="off"
            // biome-ignore lint/a11y/noAutofocus: the field appears because Count it was just pressed
            autoFocus
            value={counted}
            onChange={(ev) => setCounted(decimalOnly(ev.target.value))}
          />
          <span className="unit">{label}</span>
        </span>
        <button
          type="button"
          className="icon-button"
          aria-label="Don't count it"
          title="Don't count it"
          onClick={onDone}
        >
          <X size={20} />
        </button>
      </div>
      <button
        type="submit"
        className="button primary"
        disabled={counted.trim() === "" || count.isPending}
      >
        Save count
      </button>
      <ErrorNote error={count.error} />
    </form>
  );
}
